import React, { useState } from "react";
import { Link } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import Modal from "../../components/common/Modal";
import ActionButtons from "../../components/common/ActionButtons";
import { useGetUsersQuery, useDeleteUserMutation } from "../../store/api";

const UserList = () => {
  const { data, isLoading, isError, error } = useGetUsersQuery();
  const [deleteUser] = useDeleteUserMutation();

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    userId: null,
    userName: "",
  });

  const users = Array.isArray(data) ? data : [];

  const handleDelete = (userId, userName) => {
    setDeleteModal({ isOpen: true, userId, userName });
  };

  const confirmDelete = async () => {
    try {
      await deleteUser(deleteModal.userId).unwrap();
      setDeleteModal({ isOpen: false, userId: null, userName: "" });
    } catch (err) {
      // Error handled by UI
    }
  };

  const columns = [
    { key: "displayName", label: "Display Name" },
    { key: "accessLevel", label: "Access Level" },
    { key: "status", label: "Status" },
    { key: "lastLogin", label: "Last Login" },
    { key: "actions", label: "Actions" },
  ];

  const formatLastLogin = (lastLoginAt) => {
    if (!lastLoginAt) return "Never";
    const date = new Date(lastLoginAt);
    return date.toLocaleString();
  };

  const rows = users.map((user) => {
    const id = user.userId || user.id;
    return {
      id,
      displayName: user.displayName || "—",
      accessLevel: user.accessLevelName || "—",
      status: (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {user.isActive ? "Active" : "Inactive"}
        </span>
      ),
      lastLogin: formatLastLogin(user.lastLoginAt),
      actions: (
        <div className="flex gap-2">
          <Link
            className="p-1 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition inline-block"
            to={`/users/${id}/edit`}
            title="Edit"
            aria-label="Edit user"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </Link>
          <button
            type="button"
            className="p-1 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded transition"
            onClick={() => handleDelete(id, user.displayName)}
            title="Delete"
            aria-label="Delete user"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        subtitle="Manage user accounts and access."
        actions={
          <Link to="/users/new">
            <Button size="sm">Add User</Button>
          </Link>
        }
      />

      <Card className="p-0">
        <div className="border-b border-slate-100 dark:border-slate-700 px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
          All Users
        </div>
        <div className="px-6 py-4">
          {isLoading && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Loading users...
            </p>
          )}
          {isError && (
            <Alert tone="error">
              {error?.data?.detail || "Failed to load users."}
            </Alert>
          )}
          {!isLoading && !isError && (
            <Table columns={columns} rows={rows} emptyText="No users yet." />
          )}
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, userId: null, userName: "" })
        }
        title="Delete User"
        confirmText="Delete"
        confirmVariant="primary"
        onConfirm={confirmDelete}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.userName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default UserList;
